/**
 * 講義ステータス取得 API
 *
 * Question Backend CDK の REST API を呼び出し、
 * 現在の講義ステータスを取得する。
 * リロード時やページ初回読み込み時に使用し、
 * WebSocket が受信できなかったステータス変更を補完する。
 */

import { ensureFrontendSettings } from './frontendSettings';

export type LectureStatus = 'ready' | 'running' | 'ended';

interface StatusHistoryEntry {
  tenantLectureId: string;
  status: LectureStatus;
  timestamp: number;
  changed_by: string;
  created_at: string;
  tenant_id: string;
  lecture_id: string;
  ttl?: number;
}

interface StatusHistoryResponse {
  tenant_id: string;
  lecture_id: string;
  history: StatusHistoryEntry[];
  count: number;
}

/**
 * 講義ステータス履歴から最新のステータスを取得する。
 * 履歴が存在しない場合は null を返す。
 */
export async function fetchCurrentLectureStatus(
  tenantId: string,
  lectureId: string,
): Promise<LectureStatus | null> {
  const settings = await ensureFrontendSettings().catch(() => null);
  const apiEndpoint = settings?.questionRestApiUrl;

  if (!apiEndpoint) {
    console.warn('[LectureStatusAPI] テスト問題のREST API URLが未設定のためスキップ');
    return null;
  }

  try {
    const url = `${apiEndpoint}/lecture-status/history?tenantId=${tenantId}&lectureId=${lectureId}&limit=1`;
    // 受講生向けエンドポイントも共有キー認証が必須になった（SEC-C3 暫定対応）
    const headers: Record<string, string> = {};
    if (settings?.questionApiKey) {
      headers['x-api-key'] = settings.questionApiKey;
    }
    const response = await fetch(url, { headers });

    if (!response.ok) {
      console.warn('[LectureStatusAPI] ステータス取得失敗:', response.status, response.statusText);
      return null;
    }

    const result: StatusHistoryResponse = await response.json();

    const latest = result.history?.[0];
    if (latest) {
      console.log('[LectureStatusAPI] 最新ステータス:', latest.status);
      return latest.status;
    }

    return null;
  } catch (error) {
    console.warn('[LectureStatusAPI] ステータス取得エラー:', error);
    return null;
  }
}
